// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface VideoMembershipAccess {
  hasAccess: boolean;
  requiredMembership: any | null;
  userMemberships: any[];
  loading: boolean;
}

/**
 * Hook to check if user has access to video content based on addon membership slug
 */
export const useVideoMembershipAccess = (membershipSlug: string | null): VideoMembershipAccess => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [requiredMembership, setRequiredMembership] = useState<any>(null);
  const [userMemberships, setUserMemberships] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      console.log('🎥 VideoMembershipAccess: Starting check for slug:', membershipSlug, 'user:', user?.id);
      setLoading(true);
      
      try {
        // If no required membership slug, access is granted
        if (!membershipSlug) {
          console.log('🎥 VideoMembershipAccess: No slug required, granting access');
          setHasAccess(true);
          setRequiredMembership(null);
          setUserMemberships([]);
          return;
        }

        // If no user, no access
        if (!user) {
          console.log('🎥 VideoMembershipAccess: No user, denying access');
          setHasAccess(false);
          setRequiredMembership(null);
          setUserMemberships([]);
          return;
        }

        // Fetch required membership details by slug
        const { data: membershipData, error: membershipError } = await supabase
          .from('memberships')
          .select('*')
          .eq('slug', membershipSlug)
          .eq('is_addon', true)
          .single();

        if (membershipError) {
          console.error('Error fetching required membership:', membershipError);
          setRequiredMembership(null);
        } else {
          setRequiredMembership(membershipData);
        }

        // Get user's profile ID first
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (profileError) {
          console.error('🎥 VideoMembershipAccess: Error fetching user profile:', profileError);
          setHasAccess(false);
          setUserMemberships([]);
          return;
        }

        console.log('🎥 VideoMembershipAccess: Found profile ID:', profileData.id);

        // Fetch user's active addon memberships from profile_memberships
        const { data: userMembershipsData, error: userMembershipError } = await supabase
          .from('profile_memberships')
          .select(`
            *,
            membership:membership_slug (
              id,
              name,
              slug,
              is_addon
            )
          `)
          .eq('profile_id', profileData.id)
          .eq('is_active', true);

        if (userMembershipError) {
          console.error('🎥 VideoMembershipAccess: Error fetching user memberships:', userMembershipError);
          setUserMemberships([]);
        } else {
          console.log('🎥 VideoMembershipAccess: Raw memberships data:', userMembershipsData);
          
          const addonMemberships = (userMembershipsData || []).filter(
            membership => membership.membership?.is_addon === true
          );
          console.log('🎥 VideoMembershipAccess: Filtered addon memberships:', addonMemberships);
          
          setUserMemberships(addonMemberships);
          
          // Check if user has the required addon membership
          const hasRequiredMembership = addonMemberships.some(
            membership => membership.membership?.slug === membershipSlug
          ) || false;
          
          console.log('🎥 VideoMembershipAccess: Has required membership?', hasRequiredMembership, 'for slug:', membershipSlug);
          setHasAccess(hasRequiredMembership);
        }

      } catch (error) {
        console.error('Error checking video membership access:', error);
        setHasAccess(false);
        setRequiredMembership(null);
        setUserMemberships([]);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [membershipSlug, user?.id]);

  return {
    hasAccess,
    requiredMembership,
    userMemberships,
    loading
  };
};